import React, { useEffect, useState } from 'react'
import Layout from '@/components/layout/Layout'
import MainHeading from '@/components/common/MainHeading'
import EmployeeProfileDetailView from '@/components/common/EmployeeProfileDetailView'
import { useParams, useNavigate } from 'react-router-dom'
import { RoutesLink } from '@/utility/RouteLinks'
import EmployeeOnBoardService from '@/services/EmployeeOnBoard/EmployeeOnBoardService'
import { ThrowError } from '@/api/callApi'
import type { IEmployee } from '@/interface/employeeOnBoard'
import { Spinner } from '@/components/ui/spinner'
import { ArrowLeft } from 'lucide-react'
import { Button } from '@/components/ui/button'

const EmployeeDetail: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const [employee, setEmployee] = useState<IEmployee | null>(null);
    const [isLoading, setIsLoading] = useState(true);

    useEffect(() => {
        if (!id) return;
        const fetchEmployee = async () => {
            try {
                setIsLoading(true);
                const response = await EmployeeOnBoardService.getEmployeeById(id);
                if (response?.status && response?.data) {
                    setEmployee(response.data);
                }
            } catch (error) {
                console.error("Error fetching employee details:", error);
                ThrowError(error);
            } finally {
                setIsLoading(false);
            }
        };
        fetchEmployee();
    }, [id]);

    if (isLoading) {
        return (
            <Layout>
                <div className="flex items-center justify-center min-h-[400px]">
                    <Spinner className="h-8 w-8 text-primary animate-spin" />
                </div>
            </Layout>
        );
    }

    if (!employee) {
        return (
            <Layout>
                <div className="text-center py-12">
                    <h3 className="text-lg font-semibold text-gray-700">Employee not found</h3>
                    <Button onClick={() => navigate(RoutesLink.superAdminEmplyoeeOnboard)} className="mt-4">
                        <ArrowLeft className="w-4 h-4 mr-2" /> Back to Employees
                    </Button>
                </div>
            </Layout>
        );
    }

    return (
        <Layout>
            <div className="space-y-6">
                <MainHeading
                    title={`${employee?.name || 'Employee'}'s Profile`}
                    isBackButtonVisible
                />
                <EmployeeProfileDetailView employee={employee} />
            </div>
        </Layout>
    );
};

export default EmployeeDetail;
